import type {
  CreateLeaderboardRequest,
  Leaderboard,
  PutLeaderboardRequest,
} from '@brush/api-client';
import { readScope, scopeKey, toScopeDocument, type PointsScope } from '@brush/ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { btnGhost, btnPrimary, Modal, modalInput } from '../Modal';
import { LeaderboardPointsSection } from './LeaderboardPointsSection';

const monthBounds = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

export function LeaderboardBuilder({
  initial,
  roomId,
  contestCount,
  isAdmin,
  saving,
  deleting,
  onCreate,
  onUpdate,
  onDelete,
  onClose,
}: {
  initial: Leaderboard | null;
  roomId: string;
  /** Fantasy contests attached to this leaderboard — deleting affects them. */
  contestCount: number;
  /** §8.1's drift report is admin-only on the server, so only an administrator asks for it. */
  isAdmin: boolean;
  saving: boolean;
  deleting: boolean;
  onCreate: (body: Omit<CreateLeaderboardRequest, 'roomId'>) => void;
  onUpdate: (body: PutLeaderboardRequest) => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const bounds = monthBounds();
  const [name, setName] = useState(initial?.name ?? '');
  const [dateStart, setDateStart] = useState(initial?.dateStart ?? bounds.start);
  const [dateEnd, setDateEnd] = useState(initial?.dateEnd ?? bounds.end);
  const [confirmDelete, setConfirmDelete] = useState(false);

  /*
    Whether this dialog was opened on **New** and has since been saved.

    Creating no longer closes the dialog — a points system is attached by Start and by nothing
    else, so the picker below can only be *kept* across the save rather than applied by it, and
    closing discarded it. But a dialog that simply stays put reads as a save that did not happen,
    so the transition is stated: the title changes, Delete appears, Start unblocks, and this says
    why. Derived from `initial` going null → set, which is exactly that transition and needs no
    prop of its own.
  */
  const openedOnNew = useRef(initial === null);
  const [justCreated, setJustCreated] = useState(false);
  useEffect(() => {
    if (openedOnNew.current && initial) setJustCreated(true);
  }, [initial]);

  // §9.9's scope. Null is the ordinary state and means "everything between the dates above" —
  // the fallback the server itself applies, not a missing value.
  const [scope, setScope] = useState<PointsScope | null>(() => readScope(initial?.scope));

  /*
    The points system, which is a **field of the leaderboard** and saves with this form.

    It used to live inside the Start block, on the reasoning that a system has no effect until it
    is pinned. What that cost was the operator: they chose a system, pressed Save, and the choice
    was gone — so setting a board up meant choosing it twice, once for nothing. It is one column
    server-side, editable while the board is a Draft and frozen the moment it is not, so §2.3's
    pin is unchanged; what moved is when the choice is *recorded*, not when it binds.

    `versionNumber` null means "the newest published one", which is what the server does with an
    absent version — so the two agree about the default rather than the client picking one.
  */
  const [systemId, setSystemId] = useState(initial?.pointsSystemId ?? '');
  const [versionNumber, setVersionNumber] = useState<number | null>(
    initial?.pointsSystemVersionNumber ?? null,
  );

  /**
   * Whether the scope on screen differs from the one the server holds.
   *
   * Derived from draft-versus-stored rather than tracked as a flag, the way the template editor's
   * dirty state is — a successful save clears it with nothing having to remember the save
   * happened. It gates Start, which must not pin a version against a scope the server has not got.
   */
  const scopeDirty = useMemo(() => {
    const stored = readScope(initial?.scope);
    if (stored === null || scope === null) return (stored === null) !== (scope === null);

    // Compared with the dates the save will actually write, not the ones the scope state happens
    // to hold — otherwise moving the board's Start date would leave Start enabled against a scope
    // the server is about to receive differently, or leave it disabled when the save really does
    // change something.
    const outgoing = { ...scope, startDate: dateStart, endDate: dateEnd };
    return scopeKey(stored) !== scopeKey(outgoing);
  }, [initial?.scope, scope, dateStart, dateEnd]);

  /*
    Adopt what the server resolved the selection to.

    `versionNumber` null means "the newest published one", and the server answers with the number
    it actually resolved — so after a save the stored value is `3` where the form still holds
    `null`, and a bare comparison would leave `systemDirty` stuck true and Start blocked forever on
    a board that had just been saved correctly.

    Keyed on the stored pair, so a background refetch carrying the same values does not fire and
    cannot overwrite an edit in progress: only a change on the server reaches this.
  */
  useEffect(() => {
    if (!initial?.pointsSystemId) return;
    setSystemId(initial.pointsSystemId);
    setVersionNumber(initial.pointsSystemVersionNumber ?? null);
  }, [initial?.pointsSystemId, initial?.pointsSystemVersionNumber]);

  /**
   * Whether the selection on screen differs from the one the server holds. Gates Start for the
   * same reason `scopeDirty` does: starting pins what is *stored*, so a pin taken against an
   * unsaved choice would score a board nobody had set up.
   *
   * Compared against the stored version *number* rather than the id, because that is what the
   * picker edits — and a null here reads as "newest published", which is what the server resolves
   * an absent number to.
   */
  const systemDirty =
    systemId !== (initial?.pointsSystemId ?? '') ||
    (systemId !== '' && versionNumber !== (initial?.pointsSystemVersionNumber ?? null));

  const datesValid = dateStart <= dateEnd;
  const canSave = name.trim().length > 0 && datesValid && !saving;

  const save = () => {
    const payload = {
      name: name.trim(),
      dateStart,
      dateEnd,

      // Sent back exactly as it arrived, and **not** editable here. `tournamentIds` is the
      // fantasy layer's list (locked rule 1); this dialog is the leaderboard, which is scope,
      // points system and recalculation. Round-tripping it is the important half: a PUT replaces
      // the whole resource, so a form that quietly dropped a field it no longer shows would send
      // `[]` and wipe the list on every save — the exact hazard that keeps `defaultTags` off the
      // room form.
      tournamentIds: initial?.tournamentIds ?? [],

      // Empty means nothing chosen, which the contract spells as null — and null on a PUT clears
      // the selection rather than leaving it alone, the same meaning `scope` already gives it.
      // Refused by the server once the board has started, which is §2.3 rather than a form rule.
      pointsSystemId: systemId === '' ? null : systemId,
      pointsSystemVersion: systemId === '' ? null : versionNumber,

      // Null clears it, which is what the PUT means and what "score everything between the dates"
      // is. The server refuses a *change* on a closed board rather than a request that carries one.
      //
      // The dates are overwritten from the form rather than taken from the scope state, and this
      // is the authoritative half of the rule the section displays: a leaderboard has exactly one
      // date range. Without it, editing Start after setting a filter would save a document still
      // pinned to the old window — the board's header and its scoring would disagree, silently.
      scope:
        scope === null
          ? null
          : (toScopeDocument({
              ...scope,
              startDate: dateStart,
              endDate: dateEnd,
            }) as PutLeaderboardRequest['scope']),
    };
    if (initial) onUpdate(payload);
    else onCreate(payload);
  };

  return (
    <Modal
      title={initial ? 'Edit leaderboard' : 'New leaderboard'}
      subtitle="Which events count, what a finish is worth, and when the standings refresh."
      onClose={onClose}
      width="max-w-4xl"
      footer={
        <>
          {initial ? (
            <button
              type="button"
              onClick={() => (confirmDelete ? onDelete() : setConfirmDelete(true))}
              disabled={deleting}
              className="rounded-lg border border-chip-500/40 px-4 py-2 text-sm text-chip-500 hover:bg-chip-500/5 disabled:opacity-50"
            >
              {deleting ? 'Deleting…' : confirmDelete ? 'Confirm delete' : 'Delete'}
            </button>
          ) : null}
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button type="button" onClick={save} disabled={!canSave} className={btnPrimary}>
            {saving ? 'Saving…' : 'Save leaderboard'}
          </button>
        </>
      }
    >
      <div className="space-y-6">
        {justCreated ? (
          <p className="rounded-lg border border-felt-700/25 bg-felt-50 p-3 text-xs text-ink-900">
            <strong>Leaderboard saved</strong>, points system and all. It is not scoring yet:
            starting it below is what pins that version, and the pin is the one thing about a
            leaderboard that can never be changed afterwards.
          </p>
        ) : null}

        {confirmDelete && contestCount > 0 ? (
          <p className="rounded-lg border border-chip-500/30 bg-chip-500/5 p-3 text-xs text-ink-900">
            {contestCount} fantasy contest{contestCount === 1 ? '' : 's'} attached to this leaderboard will be
            affected by deleting it.
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs text-ink-800/60">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. April monthly leaderboard"
              className={modalInput}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-ink-800/60">Start date</span>
            <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} className={modalInput} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-ink-800/60">End date</span>
            <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} className={modalInput} />
          </label>
        </div>
        {!datesValid ? (
          <p className="text-xs text-chip-500">The end date needs to be on or after the start date.</p>
        ) : null}

        {/* §9.9. Which events this board scores, what a finish is worth, and what refreshes it. */}
        <LeaderboardPointsSection
          leaderboard={initial}
          roomId={roomId}
          scope={scope}
          onScopeChange={setScope}
          dateStart={dateStart}
          dateEnd={dateEnd}
          isAdmin={isAdmin}
          scopeDirty={scopeDirty}
          systemId={systemId}
          versionNumber={versionNumber}
          onSelectSystem={(nextSystem, nextVersion) => {
            setSystemId(nextSystem);
            setVersionNumber(nextVersion);
          }}
          systemDirty={systemDirty}
        />

      </div>
    </Modal>
  );
}
